import { EntityTemplateField, SystemStandardField, TemplateEntityType } from "../EntityTemplate.types.ts";
import { SystemModel } from "../../system/systemEdit/SystemEdit.types.ts";
import { ContractModel } from "../../contract/contract/Contract.types.ts";
import { buildContractDefaults, buildSystemDefaults, isGroupCompaniesField, readContractDefault, readSystemDefault } from "./templateDefaults.ts";

// The default is picked with the input the create pane shows, fed a form holding only this field's default,
// so the value it produces is the one the create pane would send. Group companies are the exception: the create
// pane never shows them, and their own input picks companies, which a template must not hold, so only the
// checkbox is offered
export function useFieldDefaultValueEditor(templateEntityType: TemplateEntityType, field: EntityTemplateField, onChange: (value: unknown) => void) {
    const editor = isGroupCompaniesField(field) ? "groupCompanies" : templateEntityType === TemplateEntityType.System ? "system" : "contract";
    // Creation grants access to all group companies unless the template says otherwise
    const allGroupEntityAccess = (field.defaultValue as boolean | undefined) ?? true;

    const systemDraft = buildSystemDefaults([field]);
    const contractDraft = buildContractDefaults([field]);

    const onSystemChange = async (changeFunc: (system: SystemModel) => void) => {
        const system = { ...systemDraft.system } as SystemModel;
        changeFunc(system);
        onChange(readSystemDefault({ ...systemDraft, system }, field));
    };

    const onSystemTagsChange = (tagIds: Array<string>) => onChange(readSystemDefault({ ...systemDraft, tagIds }, field));

    const onSystemCreationFieldChange = (standardField: SystemStandardField, selectedIds: Array<string>) =>
        onChange(
            readSystemDefault({ ...systemDraft, creationFieldSelections: { ...systemDraft.creationFieldSelections, [standardField]: selectedIds } }, field)
        );

    const onContractChange = async (changeFunc: (contract: ContractModel) => void) => {
        const contract = { ...contractDraft.contract } as ContractModel;
        changeFunc(contract);
        onChange(readContractDefault({ ...contractDraft, contract }, field));
    };

    const onContractTagsChange = (tagIds: Array<string>) => onChange(readContractDefault({ ...contractDraft, tagIds }, field));

    const onContractPartiesChange = (counterPartyLegalEntityIds: Array<string>) =>
        onChange(readContractDefault({ ...contractDraft, counterPartyLegalEntityIds }, field));

    return {
        editor,
        allGroupEntityAccess,
        systemDraft,
        contractDraft,
        onSystemChange,
        onSystemTagsChange,
        onSystemCreationFieldChange,
        onContractChange,
        onContractTagsChange,
        onContractPartiesChange,
    };
}
